import { useEffect, useRef, useState } from "react";

export type PresenceEntry<T> = {
  /** Stable key for React reconciliation. */
  key: string;
  /** The latest item data while present; the last-seen data while exiting. */
  item: T;
  /** True once the item has left the incoming set and is animating out. */
  exiting: boolean;
};

/**
 * Tracks a keyed list across renders so removed items stay rendered (flagged
 * `exiting`) until their exit animation finishes, then drops them. Mirrors the
 * AnimatePresence pattern, but controlled so callers can drive a prop-based
 * exit animation (markers live inside Google Maps AdvancedMarker, whose portal
 * DOM is torn down on unmount — a React-unmount-driven exit can't play there).
 *
 * `getKey` must be stable (module-level or memoized) — it is an effect dep.
 */
export function useMarkerPresence<T>(
  items: readonly T[],
  getKey: (item: T) => string,
  exitMs: number,
): PresenceEntry<T>[] {
  const [rendered, setRendered] = useState<PresenceEntry<T>[]>(() =>
    items.map((item) => ({ key: getKey(item), item, exiting: false })),
  );
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  // Reconcile incoming items against what's currently rendered.
  useEffect(() => {
    const incoming = new Map(items.map((it) => [getKey(it), it] as const));

    setRendered((prev) => {
      const seen = new Set<string>();
      const next: PresenceEntry<T>[] = [];

      for (const entry of prev) {
        seen.add(entry.key);
        const fresh = incoming.get(entry.key);
        if (fresh !== undefined) {
          // Still present (or returned mid-exit): cancel any pending removal
          // and refresh the data.
          const timer = timers.current.get(entry.key);
          if (timer) {
            clearTimeout(timer);
            timers.current.delete(entry.key);
          }
          next.push({ key: entry.key, item: fresh, exiting: false });
        } else if (entry.exiting) {
          // Already animating out — keep until its timer fires.
          next.push(entry);
        } else {
          // Just removed — begin exit, keep last-seen data.
          next.push({ ...entry, exiting: true });
        }
      }

      // Brand-new items.
      for (const [key, item] of incoming) {
        if (!seen.has(key)) next.push({ key, item, exiting: false });
      }

      return next;
    });
  }, [items, getKey]);

  // Schedule removal for anything currently exiting.
  useEffect(() => {
    for (const entry of rendered) {
      if (entry.exiting && !timers.current.has(entry.key)) {
        const timer = setTimeout(() => {
          timers.current.delete(entry.key);
          setRendered((prev) => prev.filter((e) => e.key !== entry.key));
        }, exitMs);
        timers.current.set(entry.key, timer);
      }
    }
  }, [rendered, exitMs]);

  // Clear all pending timers on unmount.
  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const timer of map.values()) clearTimeout(timer);
      map.clear();
    };
  }, []);

  return rendered;
}
